import {formatPropertyAddressLine} from "@/src/modules/properties/lib/property-address";
import type {Property} from "@/src/modules/properties/types";

import type {HouseholdAssistCandidate, HouseholdAssistResident} from "../types/household-assist";

/** Property address: number street, unit_number, city, state, zipcode. */
export function formatDispatchPropertyAddressLine(property: Property): string {
  return formatPropertyAddressLine(property);
}

/** Property one-liner: `Prop2 (Residential) · 123 Main, City ST 12345`. */
export function formatDispatchPropertyLine(property: Property): string {
  const title = formatDispatchPropertyTitle(property);
  const address = formatDispatchPropertyAddressLine(property);
  if (address) return `${title} · ${address}`;
  return title;
}

/** Property title line: `Prop2 (Residential)`. */
export function formatDispatchPropertyTitle(property: Property): string {
  const typeName = property.type?.name?.trim();
  if (!typeName) return property.name;
  return `${property.name} (${typeName})`;
}

function formatHouseholdStreetLine(candidate: HouseholdAssistCandidate): string {
  return [candidate.number, candidate.street].filter(Boolean).join(" ").trim();
}

/** Household line: name — number street — Unit #n — PIN …. */
export function formatDispatchHouseholdLine(candidate: HouseholdAssistCandidate): string {
  const parts: string[] = [];

  const name = candidate.name?.trim();
  if (name) parts.push(name);

  const streetLine = formatHouseholdStreetLine(candidate);
  if (streetLine) parts.push(streetLine);

  const unit = candidate.unit_number?.trim();
  if (unit) parts.push(`Unit #${unit}`);

  if (candidate.pin != null && String(candidate.pin).trim() !== "") {
    parts.push(`PIN ${candidate.pin}`);
  }

  return parts.join(" — ");
}

const PLACEHOLDER_DISPLAY_NAMES = new Set(["resident unit", "resident"]);

/** Skip placeholder / vacant resident rows on the desk. */
export function isDispatchAssistPlaceholderResident(resident: HouseholdAssistResident): boolean {
  if (resident.is_placeholder) return true;

  const display = resident.display_name.trim().toLowerCase();
  if (PLACEHOLDER_DISPLAY_NAMES.has(display)) return true;

  const first = resident.first_name.trim().toLowerCase();
  const last = resident.last_name.trim().toLowerCase();
  if (first === "resident" && (last === "unit" || !last)) return true;

  return !first && !last;
}

/** First real resident name for the desk, or null when none. */
export function formatDispatchResidentLine(candidate: HouseholdAssistCandidate): string | null {
  const resident = candidate.residents.find((r) => !isDispatchAssistPlaceholderResident(r));
  if (!resident) return null;

  const name = [resident.first_name, resident.last_name].map((s) => s.trim()).filter(Boolean).join(" ");
  return name || null;
}
